import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";

// Feature and limit constants
export const Features = {
  AI_FEATURES: "ai_features",
  EMAIL_NOTIFICATIONS: "email_notifications",
  CUSTOM_BRANDING: "custom_branding",
  PRIORITY_SUPPORT: "priority_support",
};

export const Limits = {
  MAX_PROGRAMS: "max_programs",
  MAX_STAFF: "max_staff",
  TRANSACTION_FEE: "transaction_fee_percent",
};

// Tier display info — Transaction-only pricing (Free + Pro)
export const TierInfo = {
  free: {
    name: "Free",
    color: "bg-slate-100 text-slate-700",
    badgeColor: "bg-slate-500",
    icon: "Clock",
    transactionFee: "5%",
  },
  pro: {
    name: "Pro",
    color: "bg-indigo-100 text-indigo-700",
    badgeColor: "bg-indigo-500",
    icon: "Zap",
    transactionFee: "3%",
  },
  // Legacy mappings
  plus: {
    name: "Pro",
    color: "bg-indigo-100 text-indigo-700",
    badgeColor: "bg-indigo-500",
    icon: "Zap",
    transactionFee: "3%",
  },
  premium: {
    name: "Pro",
    color: "bg-indigo-100 text-indigo-700",
    badgeColor: "bg-indigo-500",
    icon: "Zap",
    transactionFee: "3%",
  },
};

const FeatureGateContext = createContext(null);

export const FeatureGateProvider = ({ children, orgId }) => {
  const { token } = useAuth();
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeatures = useCallback(async () => {
    if (!orgId || !token) {
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(`${API}/organizations/${orgId}/features`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeatures(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching features:", err);
      setError(err.response?.data?.detail || "Failed to load features");
    } finally {
      setLoading(false);
    }
  }, [orgId, token]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // Check if a specific feature is enabled
  const hasFeature = useCallback(
    (featureName) => {
      if (!features) return false;
      return features.features?.[featureName] === true;
    },
    [features]
  );

  // Get a specific limit value
  const getLimit = useCallback(
    (limitName) => {
      if (!features) return 0;
      return features.limits?.[limitName] ?? 0;
    },
    [features]
  );

  // Check if a limit allows more (returns true if under limit or unlimited)
  const canAdd = useCallback(
    (limitType) => {
      if (!features?.usage) return true;
      const usage = features.usage[limitType];
      if (!usage) return true;
      if (usage.unlimited) return true;
      return usage.current < usage.limit;
    },
    [features]
  );

  // Get usage info for a limit type
  const getUsage = useCallback(
    (limitType) => {
      if (!features?.usage) return { current: 0, limit: 0, remaining: 0, unlimited: false };
      return features.usage[limitType] || { current: 0, limit: 0, remaining: 0, unlimited: false };
    },
    [features]
  );

  // Get the current tier
  const getTier = useCallback(() => {
    return features?.tier || "free";
  }, [features]);

  // Get tier display info
  const getTierInfo = useCallback(() => {
    const tier = getTier();
    return TierInfo[tier] || TierInfo.free;
  }, [getTier]);

  // Check if upgrade is needed for a feature
  const needsUpgradeFor = useCallback(
    (featureName) => {
      return !hasFeature(featureName);
    },
    [hasFeature]
  );

  // Get upgrade message for a feature
  const getUpgradeMessage = useCallback(
    (featureName) => {
      const tier = getTier();
      const featureNames = {
        [Features.AI_FEATURES]: "AI Features",
        [Features.EMAIL_NOTIFICATIONS]: "Email Notifications",
        [Features.CUSTOM_BRANDING]: "Custom Branding",
        [Features.TOURNAMENT_MANAGEMENT]: "Tournament Management",
        [Features.LEAGUE_MANAGEMENT]: "League Management",
        [Features.PRIORITY_SUPPORT]: "Priority Support",
      };

      const displayName = featureNames[featureName] || featureName;
      
      if (tier === "free") {
        return `${displayName} requires Plus or Premium plan`;
      } else if (tier === "plus") {
        return `${displayName} requires Premium plan`;
      }
      return null;
    },
    [getTier]
  );

  const value = {
    features,
    loading,
    error,
    hasFeature,
    getLimit,
    canAdd,
    getUsage,
    getTier,
    getTierInfo,
    needsUpgradeFor,
    getUpgradeMessage,
    refetch: fetchFeatures,
  };

  return (
    <FeatureGateContext.Provider value={value}>
      {children}
    </FeatureGateContext.Provider>
  );
};

export const useFeatureGate = () => {
  const context = useContext(FeatureGateContext);
  if (!context) {
    // Return a default implementation for components outside the provider
    return {
      features: null,
      loading: false,
      error: null,
      hasFeature: () => true,
      getLimit: () => -1,
      canAdd: () => true,
      getUsage: () => ({ current: 0, limit: -1, remaining: -1, unlimited: true }),
      getTier: () => "premium",
      getTierInfo: () => TierInfo.premium,
      needsUpgradeFor: () => false,
      getUpgradeMessage: () => null,
      refetch: () => {},
    };
  }
  return context;
};

export default FeatureGateContext;
